"use client";

import { useEffect } from "react";
import { isAbortError } from "@/lib/hooks/knock-utils";

/**
 * In development the stack includes the full module path
 * (`@knocklabs/client`); in production the code is bundled into numbered
 * webpack chunks, but Knock's internal function name `batchUpdateStatuses`
 * survives minification.
 */
const KNOCK_STACK_PATTERNS = [/@knocklabs\/client/, /batchUpdateStatuses/];

function _isKnockAbortError(reason: unknown): boolean {
  if (!isAbortError(reason)) {
    return false;
  }

  if (reason instanceof Error && reason.stack) {
    return KNOCK_STACK_PATTERNS.some((pattern) => pattern.test(reason.stack!));
  }

  return true;
}

/**
 * Suppresses unhandled promise rejections caused by aborted Knock API
 * requests (e.g. when the user navigates away while the Knock
 * NotificationFeed component has in-flight batchUpdateStatuses calls).
 *
 * These are benign — the request was simply cancelled because the page
 * is no longer visible — and should not surface as errors in Datadog.
 */
export function KnockAbortRecovery() {
  useEffect(() => {
    const handler = (event: PromiseRejectionEvent) => {
      if (_isKnockAbortError(event.reason)) {
        event.preventDefault();
      }
    };

    window.addEventListener("unhandledrejection", handler);
    return () => window.removeEventListener("unhandledrejection", handler);
  }, []);

  return null;
}
